import { useState } from 'react';
import betClient from '../../api/betClient';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { renderInlineMarkdown } from '../../utils/renderMarkdown';
import UpgradeModal from '../upgrade/UpgradeModal';

export default function BetAiReview({ sport }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const tier = useEffectiveTier();
  const [modalOpen, setModalOpen] = useState(false);
  const locked = tier !== 'premium';

  function fetchReview(force = false) {
    setLoading(true); setError(null);
    const params = new URLSearchParams();
    if (sport) params.set('sport', sport);
    if (force) params.set('force', 'true');
    betClient.get(`/api/bets/ai-review?${params.toString()}`)
      .then((r) => setReview(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  return (
    <div className="rounded-md border-l-[3px] border-l-[#005BB3] dark:border-l-[#3b82f6] border border-[#E5E7EB] dark:border-[#1e2536] bg-[#F8F9FA] dark:bg-[#0f1219] px-5 py-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>AI Betting Review</h3>
        {review && !loading && !locked && (
          <button onClick={() => fetchReview(true)} className="text-[12px] text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>Regenerate</button>
        )}
      </div>

      {!review && !loading && !error && (
        <>
          <p className="text-[#6B7280] text-[13px] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>Get AI-powered analysis of your betting patterns, strengths, and areas to improve.</p>
          {locked ? (
            <button onClick={() => setModalOpen(true)}
              className="px-3 py-1.5 rounded-md bg-[#94a3b8] text-white text-[13px] font-medium cursor-pointer flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
              Get AI Review
              <span className="text-[9px] font-bold px-1 py-px rounded bg-white/20 uppercase">Premium</span>
            </button>
          ) : (
            <button onClick={() => fetchReview(false)} className="px-3 py-1.5 rounded-md bg-[#005BB3] hover:bg-[#004494] text-white text-[13px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>Get AI Review</button>
          )}
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 py-3">
          <svg className="animate-spin h-3.5 w-3.5 text-[#005BB3]" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-[#6B7280] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>Analyzing your betting history...</span>
        </div>
      )}

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] px-3 py-2 mt-1">
          <p className="text-[#DC2626] text-[12px]">Failed to load.</p>
          <button onClick={() => fetchReview(false)} className="text-[11px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {review && !loading && (
        <div>
          {(review.content || '').split('\n\n').map((para, i) => (
            <p key={i} className="text-[#1F2937] dark:text-[#8892a8] text-[14px] leading-relaxed mb-2" style={{ fontFamily: 'var(--font-sans)' }}>{renderInlineMarkdown(para)}</p>
          ))}
          {review.created_at && (
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#E5E7EB] dark:border-[#1e2536]">
              {review.cached && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F8F9FA] dark:bg-[#1a2030] text-[#6B7280]">CACHED</span>}
              <span className="text-[12px] text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>{new Date(review.created_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      <UpgradeModal open={modalOpen} onClose={() => setModalOpen(false)} suggestedTier="premium" triggerFeature="AI Betting Review" />
    </div>
  );
}
